import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Link,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { getAuth, signOut } from 'firebase/auth';
import { LuCheckCircle2, LuLock } from 'react-icons/lu';
import { getSubscriptionStatus, createCheckoutSession } from '../services/billingService';
import { logger } from '../utils/logger';

const ACTIVE_STATUSES = ['active', 'trialing'];
const SUCCESS_POLL_ATTEMPTS = 6;
const SUCCESS_POLL_DELAY_MS = 2000;

/**
 * Hard paywall for free-report accounts (requires_subscription on the user
 * doc). Legacy accounts are never flagged, so they pass straight through.
 * Fails open on errors so a billing outage can't lock out paying users.
 */
function SubscriptionGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [gated, setGated] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const billingSuccess = new URLSearchParams(window.location.search).get('billing') === 'success';

    async function check() {
      // After Stripe redirects back, the webhook may lag a few seconds —
      // poll before concluding the account is still unpaid.
      const attempts = billingSuccess ? SUCCESS_POLL_ATTEMPTS : 1;
      for (let i = 0; i < attempts; i += 1) {
        try {
          const status = await getSubscriptionStatus();
          const isGated =
            status?.requires_subscription && !ACTIVE_STATUSES.includes(status?.status);
          if (!isGated || cancelled) {
            if (!cancelled) setGated(false);
            return;
          }
        } catch (error) {
          logger.error('Subscription check failed — allowing access:', error);
          if (!cancelled) setGated(false);
          return;
        }
        if (i < attempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, SUCCESS_POLL_DELAY_MS));
        }
      }
      if (!cancelled) setGated(true);
    }

    check().finally(() => {
      if (!cancelled) setChecking(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUpgrade = useCallback(async () => {
    setRedirecting(true);
    try {
      const origin = window.location.origin;
      const { checkout_url: checkoutUrl } = await createCheckoutSession(
        `${origin}/?billing=success`,
        `${origin}/`
      );
      window.location.href = checkoutUrl;
    } catch (error) {
      logger.error('Failed to start checkout:', error);
      setRedirecting(false);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut(getAuth());
    } catch (error) {
      logger.error('Sign out failed:', error);
    }
  }, []);

  if (checking) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!gated) {
    return children;
  }

  return (
    <Flex justify="center" align="center" minH="100vh" bg="gray.50" px={4} data-testid="subscription-gate">
      <Box bg="white" borderRadius="2xl" boxShadow="lg" maxW="md" w="full" p={10} textAlign="center">
        <Icon as={LuLock} boxSize={10} color="blue.500" mb={4} />
        <Heading size="lg" mb={3}>
          Your report was just the beginning
        </Heading>
        <Text color="gray.600" mb={6}>
          Your market report PDF is in your inbox. Unlock the live dashboard to
          see everything behind it — updated as new data comes in.
        </Text>
        <List spacing={2} textAlign="left" mb={8}>
          {[
            'Competitor SWOT & market intelligence',
            'Revenue estimates for every competitor',
            'Keyword & web-traffic intelligence',
            'Monthly refreshed review analytics',
          ].map((feature) => (
            <ListItem key={feature} color="gray.700">
              <ListIcon as={LuCheckCircle2} color="green.500" />
              {feature}
            </ListItem>
          ))}
        </List>
        <VStack spacing={3}>
          <Button
            colorScheme="blue"
            size="lg"
            w="full"
            onClick={handleUpgrade}
            isLoading={redirecting}
            data-testid="subscription-gate-upgrade"
          >
            Unlock Full Access
          </Button>
          <Link color="gray.500" fontSize="sm" onClick={handleSignOut}>
            Sign out
          </Link>
        </VStack>
      </Box>
    </Flex>
  );
}

export default SubscriptionGate;
